// src/data/hhpData.js
// SustainableWA HHP Lookup — loads live data from Firestore
// Falls back to embedded data if Firestore unavailable
// Sources: PAN HHP List 2024 + SustainableWA Winegrape Standard 2026 v2.19

import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

// Cache
let _prohibitedCache = null;
let _watchlistCache  = null;
let _cacheLoaded     = false;
let _cacheLoading    = null;

export async function loadHHPData() {
  if (_cacheLoaded) return;
  if (_cacheLoading) return _cacheLoading;

  _cacheLoading = (async () => {
    try {
      const [prohibSnap, watchSnap] = await Promise.all([
        getDocs(collection(db, 'hhp_prohibited')),
        getDocs(collection(db, 'hhp_watchlist')),
      ]);
      if (!prohibSnap.empty && !watchSnap.empty) {
        _prohibitedCache = prohibSnap.docs.map(d => d.data());
        _watchlistCache  = watchSnap.docs.map(d => d.data());
        _cacheLoaded = true;
        console.log(`HHP loaded from Firestore: ${_prohibitedCache.length} prohibited, ${_watchlistCache.length} watch list`);
      } else {
        throw new Error('Collections empty');
      }
    } catch (err) {
      console.warn('Using fallback HHP data:', err.message);
      _prohibitedCache = FALLBACK_PROHIBITED;
      _watchlistCache  = FALLBACK_WATCHLIST;
      _cacheLoaded = true;
    }
  })();

  return _cacheLoading;
}

export function normalizeName(name) {
  return name.toUpperCase().replace(/[^A-Z0-9\s,\-]/g, '').replace(/\s+/g, ' ').trim();
}

export function checkIngredientStatus(picolIngredientName) {
  const normalized = normalizeName(picolIngredientName);
  const picolBase  = normalized.split(',')[0].trim();

  const prohibited = _prohibitedCache || FALLBACK_PROHIBITED;
  const watchlist  = _watchlistCache  || FALLBACK_WATCHLIST;

  for (const item of prohibited) {
    const itemNorm = normalizeName(item.name);
    const itemBase = itemNorm.split(',')[0].trim();
    if (
      normalized === itemNorm || picolBase === itemNorm || picolBase === itemBase ||
      normalized.startsWith(itemNorm + ',') || normalized.startsWith(itemNorm + ' ') ||
      normalized.includes(itemNorm)
    ) {
      return { status: 'prohibited', name: item.name, cas: item.cas || '', convention: item.convention || '', criteria: null };
    }
  }

  for (const item of watchlist) {
    const itemNorm = normalizeName(item.name);
    const itemBase = itemNorm.split(',')[0].trim();
    if (
      normalized === itemNorm || picolBase === itemNorm || picolBase === itemBase ||
      normalized.startsWith(itemNorm + ',') || normalized.startsWith(itemNorm + ' ') ||
      itemNorm === picolBase || normalized.includes(itemNorm)
    ) {
      return { status: 'watchlist', name: item.name, cas: item.cas || '', riskCategory: item.riskCategory || '', criteria: item.criteria || null };
    }
  }

  return { status: 'clear', name: picolIngredientName, cas: '', criteria: null };
}

export function getHHPSummary(ingredients) {
  const results = ingredients.map(ing => ({
    ...checkIngredientStatus(ing.Name || ing.name),
    originalName: ing.Name || ing.name,
  }));
  return {
    prohibited: results.filter(r => r.status === 'prohibited'),
    watchlist:  results.filter(r => r.status === 'watchlist'),
    clear:      results.filter(r => r.status === 'clear'),
    all:        results,
  };
}

export function getCriteriaLabels(criteria) {
  if (!criteria) return [];
  const labels = [];
  const { acuteToxicity, longTermHealth, environmentalToxicity, conventions } = criteria;
  if (acuteToxicity?.whoIa)            labels.push('WHO Ia — Extremely Hazardous');
  if (acuteToxicity?.whoIb)            labels.push('WHO Ib — Highly Hazardous');
  if (acuteToxicity?.h330)             labels.push('Fatal if Inhaled (H330)');
  if (longTermHealth?.epaCarc)         labels.push('EPA Carcinogen');
  if (longTermHealth?.iarcCarc)        labels.push('IARC Carcinogen');
  if (longTermHealth?.ghsCarc)         labels.push('GHS Carcinogen (1A/1B)');
  if (longTermHealth?.iarcProbCarc)    labels.push('IARC Probable Carcinogen');
  if (longTermHealth?.epaProbCarc)     labels.push('EPA Probable Carcinogen');
  if (longTermHealth?.ghsMuta)         labels.push('GHS Mutagen (1A/1B)');
  if (longTermHealth?.ghsRepro)        labels.push('GHS Reproductive Toxicant (1A/1B)');
  if (longTermHealth?.euEdc)           labels.push('EU Endocrine Disruptor');
  if (longTermHealth?.ghsC2r2)         labels.push('Suspected Reproductive Toxicant');
  if (environmentalToxicity?.veryBioAcc)      labels.push('Very Bioaccumulative');
  if (environmentalToxicity?.veryPers)        labels.push('Very Persistent');
  if (environmentalToxicity?.veryToxicAq)     labels.push('Very Toxic to Aquatic Organisms');
  if (environmentalToxicity?.highlyToxicBees) labels.push('Highly Toxic to Bees');
  if (conventions?.montreal)           labels.push('Montreal Protocol');
  if (conventions?.pic)                labels.push('PIC Convention');
  if (conventions?.pop)                labels.push('POP Convention');
  return labels;
}

// Fallback data — used if Firestore is unavailable
const FALLBACK_PROHIBITED = [
  { name: 'Alachlor', cas: '15972-60-8', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Aldicarb', cas: '116-06-3', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Azinphos-methyl', cas: '86-50-0', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Benomyl', cas: '17804-35-2', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Captafol', cas: '2425-06-1', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Carbofuran', cas: '1563-66-2', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Carbosulfan', cas: '55285-14-8', convention: 'PIC*', listStatus: 'PROHIBITED' },
  { name: 'Chlordane', cas: '57-74-9', convention: 'PIC, POP', listStatus: 'PROHIBITED' },
  { name: 'DDT', cas: '50-29-3', convention: 'PIC, POP', listStatus: 'PROHIBITED' },
  { name: 'Dicofol', cas: '115-32-2', convention: 'POP*', listStatus: 'PROHIBITED' },
  { name: 'DNOC and its salts', cas: '534-52-1', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Endosulfan', cas: '115-29-7', convention: 'PIC, POP', listStatus: 'PROHIBITED' },
  { name: 'Ethylene dibromide', cas: '106-93-4', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Ethylene dichloride', cas: '107-06-2', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Ethylene oxide', cas: '75-21-8', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Fenthion', cas: '55-38-9', convention: 'PIC*', listStatus: 'PROHIBITED' },
  { name: 'Fluoroacetamide', cas: '640-19-7', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Hexachlorobenzene', cas: '118-74-1', convention: 'PIC, POP', listStatus: 'PROHIBITED' },
  { name: 'Hexachlorocyclohexane', cas: '608-73-1', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Isoxaben', cas: '82558-50-7', convention: 'Salmon-Safe', listStatus: 'PROHIBITED' },
  { name: 'Lindane', cas: '58-89-9', convention: 'PIC, POP', listStatus: 'PROHIBITED' },
  { name: 'Mercury and its compounds', cas: '7439-97-6', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Methamidophos', cas: '10265-92-6', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Methoxychlor', cas: '72-43-5', convention: 'POP', listStatus: 'PROHIBITED' },
  { name: 'Methyl bromide', cas: '74-83-9', convention: 'Montreal Protocol', listStatus: 'PROHIBITED' },
  { name: 'Monocrotophos', cas: '6923-22-4', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Norflurazon', cas: '27314-13-2', convention: 'Salmon-Safe', listStatus: 'PROHIBITED' },
  { name: 'Paraquat dichloride', cas: '1910-42-5', convention: 'PIC*', listStatus: 'PROHIBITED' },
  { name: 'Parathion', cas: '56-38-2', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Parathion-methyl', cas: '298-00-0', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Pentachlorophenol', cas: '87-86-5', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Phosphamidon', cas: '13171-21-6', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Sulfluramid', cas: '4151-50-2', convention: 'PIC, POP*', listStatus: 'PROHIBITED' },
  { name: 'Thiram in formulations with benomyl and carbofuran', cas: '137-26-8', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Tributyltin compounds', cas: '', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'Trichlorfon', cas: '52-68-6', convention: 'PIC', listStatus: 'PROHIBITED' },
  { name: 'alpha-BHC', cas: '319-84-6', convention: 'POP', listStatus: 'PROHIBITED' },
  { name: 'beta-HCH', cas: '319-85-7', convention: 'POP', listStatus: 'PROHIBITED' },
];

const FALLBACK_WATCHLIST = [
  { name: 'Abamectin', cas: '71751-41-2', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Acephate', cas: '30560-19-1', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Atrazine', cas: '1912-24-9', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Bifenthrin', cas: '82657-04-3', riskCategory: 'Long Term, Environment', listStatus: 'WATCH LIST' },
  { name: 'Boscalid', cas: '188425-85-6', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Captan', cas: '133-06-2', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Carbaryl', cas: '63-25-2', riskCategory: 'Acute, Long Term', listStatus: 'WATCH LIST' },
  { name: 'Chlorothalonil', cas: '1897-45-6', riskCategory: 'Acute, Long Term', listStatus: 'WATCH LIST' },
  { name: 'Chlorpyrifos', cas: '2921-88-2', riskCategory: 'Long Term, Environment', listStatus: 'WATCH LIST' },
  { name: 'Chlorpyrifos-methyl', cas: '5598-13-0', riskCategory: 'Long Term, Environment', listStatus: 'WATCH LIST' },
  { name: 'Cymoxanil', cas: '57966-95-7', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Cyproconazole', cas: '94361-06-5', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Cyprodinil', cas: '121552-61-2', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Deltamethrin', cas: '52918-63-5', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Diazinon', cas: '333-41-5', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Dichlorvos', cas: '62-73-7', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Difenoconazole', cas: '119446-68-3', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Dimethoate', cas: '60-51-5', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Dimethomorph', cas: '110488-70-9', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Diquat dichloride', cas: '85-00-7', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Dodine', cas: '2439-10-3', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Emamectin benzoate', cas: '155569-91-8', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Etoxazole', cas: '153233-91-1', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Famoxadone', cas: '131807-57-3', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Fenhexamid', cas: '126833-17-8', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Fenpropathrin', cas: '39515-41-8', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Fenpyroximate', cas: '134098-61-6', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Fipronil', cas: '120068-37-3', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Fluazinam', cas: '79622-59-6', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Folpet', cas: '133-07-3', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Glufosinate-ammonium', cas: '77182-82-2', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Glyphosate', cas: '1071-83-6', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Hexythiazox', cas: '78587-05-0', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Imidacloprid', cas: '138261-41-3', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Iprodione', cas: '36734-19-7', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Kresoxim-methyl', cas: '143390-89-0', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Lambda-cyhalothrin', cas: '91465-08-6', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Malathion', cas: '121-75-5', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Mancozeb', cas: '8018-01-7', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Maneb', cas: '12427-38-2', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Mefenoxam', cas: '70630-17-0', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Metalaxyl', cas: '57837-19-1', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Metconazole', cas: '125116-23-6', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Methomyl', cas: '16752-77-5', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Methoxyfenozide', cas: '161050-58-4', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Myclobutanil', cas: '88671-89-0', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Oxamyl', cas: '23135-22-0', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Paraquat', cas: '4685-14-7', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Pendimethalin', cas: '40487-42-1', riskCategory: 'Long Term, Environment', listStatus: 'WATCH LIST' },
  { name: 'Permethrin', cas: '52645-53-1', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Propiconazole', cas: '60207-90-1', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Pyraclostrobin', cas: '175013-18-0', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Pyridaben', cas: '96489-71-3', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Pyrimethanil', cas: '53112-28-0', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Quinoxyfen', cas: '124495-18-7', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Simazine', cas: '122-34-9', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Spirodiclofen', cas: '148477-71-8', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Strychnine', cas: '57-24-9', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Tebuconazole', cas: '107534-96-3', riskCategory: 'Acute, Long Term', listStatus: 'WATCH LIST' },
  { name: 'Terbufos', cas: '13071-79-9', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Tetraconazole', cas: '112281-77-3', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Thiacloprid', cas: '111988-49-9', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Thiram', cas: '137-26-8', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
  { name: 'Triadimefon', cas: '43121-43-3', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Trifloxystrobin', cas: '141517-21-7', riskCategory: 'Environment', listStatus: 'WATCH LIST' },
  { name: 'Vinclozolin', cas: '50471-44-8', riskCategory: 'Long Term', listStatus: 'WATCH LIST' },
  { name: 'Zeta-cypermethrin', cas: '52315-07-8', riskCategory: 'Acute, Environment', listStatus: 'WATCH LIST' },
  { name: 'Ziram', cas: '137-30-4', riskCategory: 'Acute', listStatus: 'WATCH LIST' },
];
